/**
 * Verschachtelte Analyse-Werte prüfen — Kontaktdaten, Verantwortlichkeiten,
 * Gesellschafteranteile.
 *
 * `fieldFormats.ts` prüft, was flach unter einem Answer-Key liegt. Die
 * fachlich größte Fläche der Analyse liegt aber **darunter**: sechs
 * Kontaktfelder je Person, drei Firmenfelder je Verantwortlichkeit, dazu die
 * Anteile — alles in Listen von Objekten. Über `FieldDef.format` sind die gar
 * nicht erreichbar (ein Feld, ein Format), und die Meldung braucht statt eines
 * flachen Keys einen **Pfad**, der sagt, an welchem Eintrag sie hängt.
 *
 * Vier Regeln, die diese Datei durchgängig einhält:
 *
 * 1. **Der Pfad benennt den Eintrag über seine Identität, nicht über seine
 *    Position.** Siehe {@link entryPath}.
 * 2. **Kein Datenverlust.** Kanonisiert wird nur, was gültig ist; Bemängeltes
 *    bleibt Zeichen für Zeichen stehen (dieselbe Invariante wie im flachen
 *    Kern).
 * 3. **Nichts blockiert.** Die Summenregel kann konstruktionsbedingt keinen
 *    Fehler erzeugen (siehe {@link SumIssue}); PROD enthält einen
 *    Gesellschafter-Datensatz mit 152 % Anteilssumme, der weiterhin ladbar,
 *    bearbeitbar und speicherbar bleiben muss.
 * 4. **Dependency-frei und rein**, wie der flache Kern: forms-core läuft auch
 *    im API-Image, wo eigene node_modules fehlen.
 */
import { formatSpec, parseDecimal, } from "./fieldFormats.js";
import { collectFields } from "./validation.js";
/* ------------------------------------------------------------------ *
 * Welches Teilfeld trägt welches Format
 * ------------------------------------------------------------------ */
/**
 * Die sechs Kontaktfelder aus `KontaktdatenGroup`. `adresse` bleibt bewusst
 * ohne Format: dort steht laut Platzhalter „Straße, PLZ, Ort" — eine
 * Freitextzeile, die weder PLZ- noch sonst eine Einzelwertprüfung besteht.
 */
export const KONTAKTDATEN_FORMATS = {
    telefonPrivat: "phone",
    telefonGeschaeftlich: "phone",
    mobilPrivat: "phone",
    mobilGeschaeftlich: "phone",
    emailPrivat: "email",
    emailGeschaeftlich: "email",
};
/**
 * `anteile` bleibt ein `string` (kein Typwechsel im jsonb) und wird als
 * Prozentwert geprüft — mit `percent` also nur warnend, weil ein Anteil über
 * 100 % im Bestand vorkommt und eine Teilerfassung fachlich legitim ist.
 *
 * `geburtsdatum` bekommt bewusst KEIN `date`-Format: beide Renderer benutzen
 * dafür `<input type="date">`, das gar keinen anderen Wert als ein ISO-Datum
 * herausgibt. Eine Prüfung könnte dort nie anschlagen — außer bei einem
 * Bestandswert, den derselbe Input dann ohnehin nicht anzeigt.
 */
export const PERSON_ENTRY_FORMATS = {
    anteile: "percent",
};
/** Die Firmenfelder einer externen Verantwortlichkeit. */
export const VERANT_ENTRY_FORMATS = {
    web: "url",
    telefon: "phone",
    email: "email",
};
/**
 * Die geprüften Felder einer Verbund-/Beteiligungsfirma.
 *
 * `anteil` ist wie bei den Gesellschafter-Anteilen `percent` und damit nur
 * warnend: Eine Quote über 100 % ist ein Tippfehler, aber kein Grund, dem
 * Berater das Speichern zu verwehren. `firmenname`, `rollen`, `verbindung`
 * und die Tätigkeitsfelder tragen bewusst kein Format — es sind Freitext
 * bzw. Auswahlwerte, deren Werteliste das Schema führt.
 */
export const FIRMA_ENTRY_FORMATS = {
    anteil: "percent",
};
/** Das verschachtelte Kontakt-Objekt heißt in beiden Eintragsarten so. */
export const KONTAKT_SUB = "kontakt";
/* ------------------------------------------------------------------ *
 * Pfade
 * ------------------------------------------------------------------ */
/** Markiert im Pfad eine Identität — „nicht die Position, sondern der Eintrag". */
export const ENTRY_ID_MARKER = "#";
const entryId = (entry) => {
    if (!entry || typeof entry !== "object")
        return "";
    const id = entry.id;
    return typeof id === "string" ? id.trim() : "";
};
/**
 * `gesellschafter[#3f9a…]` — der Eintrag wird über seine **Id** benannt, nicht
 * über seinen Index.
 *
 * Warum: Löscht der Nutzer einen Eintrag aus der Mitte, rutschen alle
 * nachfolgenden Einträge um eine Position nach vorn. Ein indexbasierter Pfad
 * (`gesellschafter[2].telefon`) zeigte danach auf den Nachbarn — die Meldung
 * zur kaputten Nummer von Eintrag 3 hinge plötzlich an Eintrag 2, dessen
 * Nummer in Ordnung ist. Beide Renderer keyen ihre React-Kinder aus genau dem
 * Grund schon heute mit `entry.id`; die Einträge tragen die Id seit ihrer
 * Anlage (`crypto.randomUUID()`), sie ist also vorhanden und stabil.
 *
 * Rückfall auf den Index nur, wenn ein Bestandseintrag gar keine Id hat (im
 * Portal-Renderer als `entry.id || index` bereits einkalkuliert). Dann ist der
 * Pfad wieder positionsabhängig — deshalb der sichtbare Unterschied im
 * Klammerinhalt: `[#…]` ist eine Identität, `[2]` eine Position.
 */
export const entryPath = (listKey, entry, index) => {
    const id = entryId(entry);
    return id
        ? `${listKey}[${ENTRY_ID_MARKER}${id}]`
        : `${listKey}[${index}]`;
};
/** `gesellschafter[#3f9a…]` + `kontakt`, `emailPrivat` → `…].kontakt.emailPrivat`. */
export const subPath = (base, ...teile) => [base, ...teile].join(".");
/**
 * DOM-Id der Meldung zu einem Pfad. Klammern und Punkte werden ersetzt, damit
 * die Id auch als CSS-Selektor taugt; die Id des Eintrags bleibt enthalten,
 * also bleibt die Id eindeutig — zwei Listen auf einer Seite
 * (Geschäftsführer und Gesellschafter) kollidieren schon über den Listen-Key
 * nicht.
 *
 * Der Identitäts-Marker `#` wird dabei zu `id-` und fällt NICHT der
 * Zeichenbereinigung zum Opfer: sonst ergäben `gesellschafter[#2]` (Eintrag
 * mit der Id „2") und `gesellschafter[2]` (Eintrag an Position 2) dieselbe
 * DOM-Id — in einer gemischten Liste aus Altdaten ohne Id und neu angelegten
 * Einträgen wäre die Zuordnung damit genau dort mehrdeutig, wo der Pfad sie
 * eindeutig machen soll.
 */
export const formatMessageId = (pfad) => `${pfad.split(ENTRY_ID_MARKER).join("id-").replace(/[^A-Za-z0-9_-]+/g, "-")}-formathinweis`;
/* ------------------------------------------------------------------ *
 * Einzelwert an einem Pfad
 * ------------------------------------------------------------------ */
const asText = (value) => {
    if (typeof value === "string")
        return value;
    if (typeof value === "number")
        return Number.isFinite(value) ? String(value) : "";
    if (typeof value === "boolean")
        return String(value);
    return "";
};
/**
 * Prüft einen verschachtelten Einzelwert und meldet ihn unter seinem Pfad.
 *
 * `key` bleibt der flache Answer-Key der Liste (`gesellschafter`) — das ist
 * die Zuordnung, die Renderer und Server bereits kennen; `path` sagt
 * zusätzlich, welcher Eintrag und welches Teilfeld gemeint ist. Ohne `format`
 * (z. B. `adresse`) wird nichts gemeldet, damit die Aufrufer die Tabelle
 * direkt nachschlagen können, ohne vorher zu verzweigen.
 */
export function validateEntryValue(key, path, format, value) {
    if (!format)
        return undefined;
    const text = asText(value);
    if (text.trim() === "")
        return undefined;
    const spec = formatSpec(format);
    if (spec.isValid(text))
        return undefined;
    return {
        key,
        path,
        format,
        message: spec.message,
        severity: spec.defaultSeverity,
        value: text,
    };
}
/**
 * Kanonische Schreibweise beim Verlassen — aber **nur, was gültig ist**.
 *
 * Gibt `undefined` zurück, wenn nichts zu schreiben ist: kein Format, leer,
 * bemängelt, oder bereits kanonisch. Ein Wert, den wir gleichzeitig
 * bemängeln, wird nie umgeschrieben — sonst kann der Nutzer die Meldung nicht
 * mehr einordnen. Ein unveränderter Wert wird nicht zurückgemeldet, damit ein
 * bloßes Durchtabben das Formular nicht „dirty" macht.
 */
export function canonicalizeEntryValue(format, roh) {
    if (!format || roh.trim() === "")
        return undefined;
    const spec = formatSpec(format);
    if (!spec.isValid(roh))
        return undefined;
    const kanonisch = spec.normalize(roh);
    return kanonisch === roh ? undefined : kanonisch;
}
/* ------------------------------------------------------------------ *
 * Ganze Einträge und Listen
 * ------------------------------------------------------------------ */
const istObjekt = (value) => !!value && typeof value === "object" && !Array.isArray(value);
const alsListe = (value) => Array.isArray(value) ? value.filter(istObjekt) : [];
/**
 * Wie `alsListe`, aber **positionserhaltend**: Nicht-Objekte werden
 * übersprungen, ohne die Nummerierung der übrigen Einträge zu verschieben.
 *
 * Das zählt für den Pfad. Beide Renderer indizieren die ungefilterte Liste
 * (`entries.map((entry, idx) => …)`); würde hier vorher gefiltert, meinten
 * `gesellschafter[1]` aus dem Kern und `gesellschafter[1]` aus dem DOM
 * verschiedene Einträge, sobald ein `null` in der jsonb-Liste steht und die
 * Einträge keine Id tragen (nur dann greift der Rückfall auf die Position).
 * Für die Summe ist die Position egal — die nutzt weiter `alsListe`.
 */
const eintraegeMitPosition = (value) => Array.isArray(value)
    ? value.flatMap((entry, index) => istObjekt(entry) ? [{ entry, index }] : [])
    : [];
/** Prüft ein Kontakt-Objekt unter dem Pfad, an dem es hängt. */
export function validateKontaktdaten(key, basisPfad, kontakt) {
    if (!istObjekt(kontakt))
        return [];
    const issues = [];
    for (const [sub, format] of Object.entries(KONTAKTDATEN_FORMATS)) {
        const issue = validateEntryValue(key, subPath(basisPfad, sub), format, kontakt[sub]);
        if (issue)
            issues.push(issue);
    }
    return issues;
}
const validateEintrag = (key, entry, pfad, formats) => {
    const issues = [];
    for (const [sub, format] of Object.entries(formats)) {
        const issue = validateEntryValue(key, subPath(pfad, sub), format, entry[sub]);
        if (issue)
            issues.push(issue);
    }
    issues.push(...validateKontaktdaten(key, subPath(pfad, KONTAKT_SUB), entry[KONTAKT_SUB]));
    return issues;
};
/** Alle Formatverstöße einer `personEntries`-Liste, je Eintrag mit Pfad. */
export const validatePersonEntries = (key, value) => eintraegeMitPosition(value).flatMap(({ entry, index }) => validateEintrag(key, entry, entryPath(key, entry, index), PERSON_ENTRY_FORMATS));
/** Alle Formatverstöße einer `verantEntries`-Liste, je Eintrag mit Pfad. */
export const validateVerantEntries = (key, value) => eintraegeMitPosition(value).flatMap(({ entry, index }) => validateEintrag(key, entry, entryPath(key, entry, index), VERANT_ENTRY_FORMATS));
/** Alle Formatverstöße einer `firmaEntries`-Liste, je Eintrag mit Pfad. */
export const validateFirmaEntries = (key, value) => eintraegeMitPosition(value).flatMap(({ entry, index }) => validateEintrag(key, entry, entryPath(key, entry, index), FIRMA_ENTRY_FORMATS));
/**
 * Verstöße eines verschachtelten Feldes. Für alle anderen Feldtypen leer —
 * die prüft `validateFieldFormat` flach weiter.
 */
export function validateEntryFieldFormats(field, value) {
    if (field.type === "personEntries")
        return validatePersonEntries(field.key, value);
    if (field.type === "verantEntries")
        return validateVerantEntries(field.key, value);
    if (field.type === "firmaEntries")
        return validateFirmaEntries(field.key, value);
    return [];
}
/**
 * Das verschachtelte Gegenstück zu `validateAnswerFormats`: prüft einen
 * ganzen Antwortsatz gegen die Teilfeld-Formate seiner Listen.
 *
 * Rein wie das flache Pendant: `answers` wird nicht verändert, es wird nichts
 * geworfen, und ein Antwortsatz ohne Listen liefert eine leere Liste.
 */
export function validateNestedAnswerFormats(schema, answers) {
    const issues = [];
    for (const field of collectFields(schema)) {
        if (!Object.prototype.hasOwnProperty.call(answers, field.key))
            continue;
        issues.push(...validateEntryFieldFormats(field, answers[field.key]));
    }
    return issues;
}
/**
 * Gesellschafteranteile. Toleranz 1 Prozentpunkt: drei gleiche Anteile werden
 * als „33,33" getippt und ergeben 99,99 % — das ist keine Unstimmigkeit,
 * sondern gerundet.
 */
export const ANTEILE_SUM_RULE = {
    sub: "anteile",
    target: 100,
    tolerance: 1,
    label: "Die Anteile",
    unit: "%",
};
/** 152 → „152", 99.99 → „99,99" — deutsche Schreibweise ohne Intl. */
const zahlText = (n) => String(Math.round(n * 100) / 100).replace(".", ",");
/**
 * Summiert ein Teilfeld über alle Einträge. Nicht lesbare Angaben („ca. 50")
 * zählen als nicht erkannt: sie werden nicht geraten und nicht als 0 gewertet,
 * sonst meldete die Regel eine Unstimmigkeit, die aus ihrer eigenen Annahme
 * stammt.
 */
export function entrySum(value, rule = ANTEILE_SUM_RULE) {
    const entries = alsListe(value);
    let summe = 0;
    let erkannt = 0;
    for (const entry of entries) {
        const n = parseDecimal(entry[rule.sub]);
        if (n === undefined)
            continue;
        summe += n;
        erkannt += 1;
    }
    return {
        summe,
        erkannt,
        gesamt: entries.length,
        vollstaendig: entries.length > 0 && erkannt === entries.length,
    };
}
/**
 * Warnung, wenn die Summe nennenswert daneben liegt — sonst `undefined`.
 *
 * Geschwiegen wird bewusst in drei Fällen, in denen eine Meldung nur Lärm
 * wäre:
 *  - leere Liste (nichts erfasst),
 *  - mindestens ein Eintrag ohne Anteil (halb ausgefülltes Formular),
 *  - mindestens eine Angabe, die sich nicht als Zahl lesen lässt.
 */
export function checkEntrySum(key, value, rule = ANTEILE_SUM_RULE) {
    const status = entrySum(value, rule);
    if (!status.vollstaendig)
        return undefined;
    if (Math.abs(status.summe - rule.target) <= rule.tolerance)
        return undefined;
    return {
        key,
        path: subPath(key, rule.sub),
        summe: status.summe,
        target: rule.target,
        message: `${rule.label} ergeben zusammen ${zahlText(status.summe)} ${rule.unit} ` +
            `statt ${zahlText(rule.target)} ${rule.unit}. Bitte prüfen — ` +
            `gespeichert wird trotzdem.`,
        severity: "warning",
    };
}
